import { Component, ElementRef, forwardRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  AbstractControl,
  ValidationErrors,
  Validator
} from '@angular/forms';

@Component({
  selector: 'wl-email-input',
  standalone: false,
  templateUrl: 'email-input.component.html',
  styleUrls: ['email-input.component.less'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => EmailInputComponent),
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => EmailInputComponent),
      multi: true
    }
  ]
})
export class EmailInputComponent implements ControlValueAccessor, Validator, OnChanges {
  @Input() label = 'Email';
  @Input() placeholder = 'email@email.com';
  @Input() required = false;
  @Input() disabled = false;
  @Input() name = '';
  @Input() id = '';
  @Input() useCustomLabel = false;

  @ViewChild('inputEl') inputEl!: ElementRef<HTMLInputElement>;

  value = ''; // ✓ Not an @Input
  touched = false;

  onChange = (value: string) => {};
  onTouched = () => {};
  onValidatorChange = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['required']) {
      this.onValidatorChange();
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const value = this.normalize(control.value);
    if (!value && this.required) {
      return { required: true };
    }
    if (value && !this.isValidEmail(value)) {
      return { email: true };
    }
    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  writeValue(value: any): void {
    const normalized = this.normalize(value);
    this.value = normalized;
    this.onChange(normalized);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.value = input.value;
    this.onChange(this.value);
  }

  markAsTouched(): void {
    this.value = this.value.trim();
    this.inputEl.nativeElement.value = this.value;
    this.onChange(this.value);
    if (!this.touched) {
      this.touched = true;
      this.onTouched();
    }
  }

  public isValidEmail(email: string): boolean {
    if (!email && !this.required) {
      return true;
    }
    const emailRegex = /^[a-z0-9!#$%&'*+\-\/=?^_`{|}~]+(?:\.[a-z0-9!#$%&'*+\-\/=?^_`{|}~]+)*@(?:[a-z0-9-]+\.)+[a-z]{2,}$/i;
    return emailRegex.test(this.normalize(email));
  }

  public hasDeliverabilityWarning(email: string): boolean {
    if (!email) return false;
    const atIndex = email.indexOf('@');
    if (atIndex < 0) return false;
    return /[!#$%&'*+\-\/=?^_`{|}~]/.test(email.substring(0, atIndex));
  }

  private normalize(value: any): string {
    return (value ?? '').toString().trim();
  }
}
